import React, { useState, useEffect, useRef } from "react";
import { customFetch } from '../apiClient';

const initialForm = { srno: 0, chargeFor: "OPD", chargeName: "", amount: "", unit: "" };

const ChargeMaster = () => {
    const [activeTab, setActiveTab] = useState('OPD'); // OPD, IPD, BILLING
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    
    // Form States
    const [formData, setFormData] = useState(initialForm);
    const [editMode, setEditMode] = useState(null); // stores SRNO of row being edited
    const [editData, setEditData] = useState({});

    const formRef = useRef(null);
  

    // --- Data Fetching ---
    const fetchServices = async () => {
        setLoading(true);
        try {
           const res = await customFetch('/ChargeMaster/list');
            if (!res.ok) throw new Error("Failed to fetch services");
            const data = await res.json();
            setServices(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchServices(); }, []);

    // --- Handlers ---
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleAddService = async (e) => {
        if (e) e.preventDefault();
        if (!formData.chargeName || !formData.amount) {
            setError("Service Name and Amount are required");
            return;
        }

        try {
            const payload = { ...formData, chargeFor: activeTab };
         const res = await customFetch('/ChargeMaster', {
    method: "POST",
    body: JSON.stringify(payload)
});

            if (!res.ok) throw new Error(await res.text());
            setSuccess("Service added successfully!");
            setFormData({ ...initialForm, chargeFor: activeTab });
            fetchServices();
            formRef.current?.querySelector('input')?.focus();
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.message);
        }
    };

    const handleInlineEditSave = async (srno) => {
        try {
           const res = await customFetch(`/ChargeMaster/${srno}`, {
    method: "PUT",
    body: JSON.stringify(editData)
});

            if (!res.ok) throw new Error("Update failed");
            setSuccess("Service updated!");
            setEditMode(null);
            fetchServices();
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.message);
        }
    };

    const handleDelete = async (srno) => {
        if (!window.confirm("Are you sure you want to delete this service?")) return;
        try {
           const res = await customFetch(`/ChargeMaster/${srno}`, {
    method: "DELETE"
});
            if (!res.ok) throw new Error("Delete failed");
            setSuccess("Service deleted!");
            fetchServices();
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.message);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            const inputs = Array.from(formRef.current.querySelectorAll('input, select, button'));
            const index = inputs.indexOf(e.target);
            if (index < inputs.length - 1) {
                e.preventDefault();
                inputs[index + 1].focus();
            } else if (index === inputs.length - 1) {
                handleAddService();
            }
        }
    };

    const filteredServices = services.filter(s => s.chargeFor === activeTab);

    return (
        <div className="charge-master-container">
            <div className="search-card">
                <div className="card-header-theme">
                    <div className="header-left">
                        <div className="header-icon"><i className="bi bi-gear-wide-connected"></i></div>
                        <div className="header-text">
                            <h5>Charge / Service Master</h5>
                            <p>Manage hospital services, rates and categories</p>
                        </div>
                    </div>
                </div>

                <div className="tabs-header">
                    <button className={`tab-btn ${activeTab === 'OPD' ? 'active' : ''}`} onClick={() => setActiveTab('OPD')}>
                        <i className="bi bi-person-badge"></i> OPD
                    </button>
                    <button className={`tab-btn ${activeTab === 'IPD' ? 'active' : ''}`} onClick={() => setActiveTab('IPD')}>
                        <i className="bi bi-hospital"></i> IPD
                    </button>
                    <button className={`tab-btn ${activeTab === 'BILLING' ? 'active' : ''}`} onClick={() => setActiveTab('BILLING')}>
                        <i className="bi bi-receipt"></i> Billing
                    </button>
                </div>

                <div className="card-content">
                    {error && (
                        <div className="alert-custom alert-danger">
                            <i className="bi bi-exclamation-triangle-fill"></i> {error}
                            <button className="alert-close" onClick={() => setError('')}><i className="bi bi-x"></i></button>
                        </div>
                    )}
                    {success && (
                        <div className="alert-custom alert-success">
                            <i className="bi bi-check-circle-fill"></i> {success}
                            <button className="alert-close" onClick={() => setSuccess('')}><i className="bi bi-x"></i></button>
                        </div>
                    )}

                    <div className="quick-entry-row" ref={formRef}>
                        <div className="entry-grid">
                            <div className="form-group flex-2">
                                <label>Service Name <span className="req">*</span></label>
                                <input 
                                    type="text" name="chargeName" 
                                    value={formData.chargeName} onChange={handleInputChange} onKeyDown={handleKeyDown}
                                    placeholder="Service Name..." className="form-input-theme" autoFocus
                                />
                            </div>
                            <div className="form-group">
                                <label>Rate (₹) <span className="req">*</span></label>
                                <input 
                                    type="number" name="amount" 
                                    value={formData.amount} onChange={handleInputChange} onKeyDown={handleKeyDown}
                                    placeholder="0.00" className="form-input-theme"
                                />
                            </div>
                            <div className="form-group">
                                <label>Unit</label>
                                <input 
                                    type="text" name="unit" 
                                    value={formData.unit} onChange={handleInputChange} onKeyDown={handleKeyDown}
                                    placeholder="e.g. Per Day" className="form-input-theme"
                                />
                            </div>
                            <div className="form-group action-btn-group">
                                <button className="btn-add-theme" onClick={handleAddService}>
                                    <i className="bi bi-plus-lg"></i> Add
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="table-section-theme">
                        <div className="table-responsive">
                            <table className="custom-table-theme">
                                <thead>
                                    <tr>
                                        <th className="col-sr">Sr.</th>
                                        <th>Service Name</th>
                                        <th className="text-right col-amt">Amount (₹)</th>
                                        <th className="col-unit">Unit</th>
                                        <th className="text-center col-action">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr><td colSpan="5" className="empty-state">Loading Services...</td></tr>
                                    ) : filteredServices.length === 0 ? (
                                        <tr><td colSpan="5" className="empty-state">No services found for {activeTab}</td></tr>
                                    ) : filteredServices.map((srv, idx) => (
                                        <tr key={srv.srno} className={editMode === srv.srno ? 'editing' : ''}>
                                            <td className="col-sr">{idx + 1}</td>
                                            <td>
                                                {editMode === srv.srno ? (
                                                    <input 
                                                        className="table-edit-input" 
                                                        value={editData.chargeName} 
                                                        onChange={(e) => setEditData({...editData, chargeName: e.target.value})}
                                                    />
                                                ) : <span className="fw-500">{srv.chargeName}</span>}
                                            </td>
                                            <td className="text-right text-primary fw-600 col-amt">
                                                {editMode === srv.srno ? (
                                                    <input 
                                                        type="number" className="table-edit-input text-right" 
                                                        value={editData.amount} 
                                                        onChange={(e) => setEditData({...editData, amount: e.target.value})}
                                                    />
                                                ) : `₹${srv.amount?.toFixed(2)}`}
                                            </td>
                                            <td className="col-unit">
                                                {editMode === srv.srno ? (
                                                    <input 
                                                        className="table-edit-input" 
                                                        value={editData.unit} 
                                                        onChange={(e) => setEditData({...editData, unit: e.target.value})}
                                                    />
                                                ) : (srv.unit || '-')}
                                            </td>
                                            <td className="text-center col-action">
                                                {editMode === srv.srno ? (
                                                    <div className="action-btns">
                                                        <button className="btn-icon-theme save" onClick={() => handleInlineEditSave(srv.srno)} title="Save"><i className="bi bi-check-lg"></i></button>
                                                        <button className="btn-icon-theme cancel" onClick={() => setEditMode(null)} title="Cancel"><i className="bi bi-x-lg"></i></button>
                                                    </div>
                                                ) : (
                                                    <div className="action-btns">
                                                        <button className="btn-icon-theme edit" onClick={() => { setEditMode(srv.srno); setEditData(srv); }} title="Edit"><i className="bi bi-pencil"></i></button>
                                                        <button className="btn-icon-theme delete" onClick={() => handleDelete(srv.srno)} title="Delete"><i className="bi bi-trash"></i></button>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            <style jsx>{`
                .charge-master-container { padding: 0; font-family: system-ui, -apple-system, sans-serif; }
                .search-card { background: white; border-radius: 6px; box-shadow: 0 1px 4px rgba(0,0,0,0.1); border: 1px solid #e2e8f0; overflow: hidden; }
                
                /* Header */
                .card-header-theme { background: var(--theme-color, #0088cc); padding: 8px 12px; border-bottom: 2px solid #00d4aa; }
                .header-left { display: flex; align-items: center; gap: 10px; color: white; }
                .header-icon { width: 24px; height: 24px; background: rgba(255, 255, 255, 0.2); border-radius: 4px; display: flex; align-items: center; justify-content: center; font-size: 14px; }
                .header-text h5 { margin: 0; font-size: 14px; font-weight: 600; }
                .header-text p { margin: 0; font-size: 10px; opacity: 0.85; }

                /* Tabs */
                .tabs-header { display: flex; background: #f8fafc; border-bottom: 1px solid #e2e8f0; padding: 0 12px; gap: 4px; }
                .tab-btn { 
                    padding: 8px 16px; border: none; background: transparent; color: #64748b; 
                    font-weight: 600; font-size: 11px; cursor: pointer; border-bottom: 2px solid transparent; 
                    display: flex; gap: 6px; align-items: center; transition: 0.2s; 
                }
                .tab-btn i { font-size: 13px; }
                .tab-btn:hover { color: var(--theme-color, #0088cc); background: rgba(0,136,204,0.05); }
                .tab-btn.active { color: var(--theme-color, #0088cc); border-bottom-color: var(--theme-color, #0088cc); background: white; font-weight: 700; }

                .card-content { padding: 12px; }
                
                /* Alerts */
                .alert-custom { margin-bottom: 12px; padding: 6px 10px; border-radius: 4px; display: flex; align-items: center; gap: 6px; font-size: 11px; font-weight: 500; position: relative; }
                .alert-danger { background: #fff5f5; color: #e53e3e; border: 1px solid #feb2b2; }
                .alert-success { background: #f0fff4; color: #27ae60; border: 1px solid #9ae6b4; }
                .alert-close { position: absolute; right: 8px; background: none; border: none; cursor: pointer; opacity: 0.6; }

                /* Form */
                .quick-entry-row { background: #f8fafc; padding: 10px; border-radius: 4px; border: 1px solid #e2e8f0; margin-bottom: 12px; }
                .entry-grid { display: grid; grid-template-columns: minmax(150px, 2fr) 1fr 1fr auto; gap: 10px; align-items: flex-end; }
                .form-group { display: flex; flex-direction: column; gap: 4px; }
                .form-group label { font-size: 10px; font-weight: 600; color: #4a5568; text-transform: uppercase; }
                .req { color: #e53e3e; }
                
                .form-input-theme { 
                    height: 28px; padding: 0 8px; border: 1px solid #cbd5e0; border-radius: 4px; 
                    font-size: 11px; background: white; transition: 0.2s; outline: none; 
                }
                .form-input-theme:focus { border-color: var(--theme-color, #3182ce); box-shadow: 0 0 0 1px var(--theme-color, #3182ce); }

                .btn-add-theme { 
                    height: 28px; padding: 0 14px; background: var(--theme-color, #3182ce); color: white; 
                    border: none; border-radius: 4px; font-size: 11px; font-weight: 600; cursor: pointer; 
                    display: flex; align-items: center; gap: 4px; transition: 0.2s; 
                }
                .btn-add-theme:hover { opacity: 0.9; }

                /* Table */
                .table-section-theme { border: 1px solid #e2e8f0; border-radius: 4px; }
                .table-responsive { overflow-x: auto; -webkit-overflow-scrolling: touch; }
                .custom-table-theme { width: 100%; border-collapse: collapse; font-size: 11px; }
                .custom-table-theme th { 
                    background: #edf2f7; color: #4a5568; padding: 8px 10px; font-weight: 600; 
                    text-transform: uppercase; text-align: left; border-bottom: 1px solid #cbd5e0; white-space: nowrap;
                }
                .custom-table-theme td { padding: 6px 10px; border-bottom: 1px solid #e2e8f0; color: #2d3748; vertical-align: middle; }
                .custom-table-theme tr:hover { background: #f7fafc; }
                .custom-table-theme tr.editing { background: #ebf8ff; }

                .col-sr { width: 40px; text-align: center; }
                .col-amt { width: 100px; }
                .col-unit { width: 120px; }
                .col-action { width: 80px; }

                .fw-500 { font-weight: 500; }
                .fw-600 { font-weight: 600; }
                .text-primary { color: var(--theme-color, #3182ce); }
                .text-right { text-align: right; }
                .text-center { text-align: center; }

                .table-edit-input { width: 100%; padding: 4px 6px; border: 1px solid var(--theme-color, #3182ce); border-radius: 3px; font-size: 11px; outline: none; }
                
                .action-btns { display: flex; gap: 4px; justify-content: center; }
                .btn-icon-theme { 
                    width: 22px; height: 22px; border-radius: 4px; border: none; 
                    display: flex; align-items: center; justify-content: center; 
                    cursor: pointer; font-size: 11px; transition: 0.2s; 
                }
                .btn-icon-theme.edit { background: #ebf8ff; color: #3182ce; }
                .btn-icon-theme.delete { background: #fff5f5; color: #e53e3e; }
                .btn-icon-theme.save { background: #c6f6d5; color: #2f855a; }
                .btn-icon-theme.cancel { background: #edf2f7; color: #4a5568; }
                .btn-icon-theme:hover { opacity: 0.8; }

                .empty-state { text-align: center; padding: 20px; color: #718096; font-style: italic; }

                @media (max-width: 768px) {
                    .entry-grid { grid-template-columns: 1fr 1fr; }
                    .flex-2 { grid-column: span 2; }
                    .action-btn-group { grid-column: span 2; }
                    .btn-add-theme { width: 100%; justify-content: center; }
                    .tab-btn { padding: 8px 12px; flex: 1; justify-content: center; }
                }
            `}</style>
        </div>
    );
};

export default ChargeMaster;